import { useState, useEffect } from 'react';
import {
  User, Shield, Bell, Palette, Lock, Monitor, Smartphone,
  Tablet, Globe, Clock, LogOut, AlertTriangle, CheckCircle,
  Loader2, RefreshCw, Eye, EyeOff, Save, Mail, Building2,
  ChevronRight, KeyRound, Trash2, MoreHorizontal, ChevronDown, Check,
  BookOpen, Play, RotateCcw, CircleDashed
} from 'lucide-react';
import useAuthStore from '../stores/authStore';
import useThemeStore from '../stores/themeStore';
import useTutorialStore from '../stores/tutorialStore';
import { allTutorials } from '../components/tutorials/configs';
import MfaSetup from '../components/MfaSetup';
import PushNotificationSettings from '../components/PushNotificationSettings';
import NotificationsTab from '../components/NotificationsTab';

const UserProfile = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const { user } = useAuthStore();

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'preferences', label: 'Preferences', icon: Palette },
    { id: 'tutorials', label: 'Tutorials', icon: BookOpen },
  ];

  return (
    <div className="page-content user-profile-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Account Settings</h1>
        <p className="page-subtitle">Manage your profile, security, and preferences</p>
      </div>
      {/* Tab Navigation */}
      <div className="user-profile-tabs">
        {/* All tabs - overflow tabs hidden on mobile via CSS */}
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={"user-profile-tab " + (activeTab === tab.id ? "active" : "") + (index >= 3 ? " overflow-tab" : "")}
          >
            <tab.icon size={18} />
            <span>{tab.label}</span>
          </button>
        ))}
        {/* More dropdown - only visible on mobile */}
        <div className="user-profile-more-wrapper">
          <button
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            className={"user-profile-tab user-profile-more-btn " + (tabs.slice(3).some(t => t.id === activeTab) ? "active" : "")}
          >
            <MoreHorizontal size={18} />
            <span>More</span>
            <ChevronDown size={14} className={showMoreMenu ? "rotated" : ""} />
          </button>
          {showMoreMenu && (
            <div className="user-profile-more-menu">
              {tabs.slice(3).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setShowMoreMenu(false);
                  }}
                  className={"user-profile-more-item " + (activeTab === tab.id ? "active" : "")}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div className="user-profile-content">
        {activeTab === 'profile' && <ProfileTab user={user} />}
        {activeTab === 'security' && <SecurityTab user={user} />}
        {activeTab === 'notifications' && <NotificationsTab user={user} />}
        {activeTab === 'preferences' && <PreferencesTab user={user} />}
        {activeTab === 'tutorials' && <TutorialsTab />}
      </div>
    </div>
  );
};

// ============================================
// PROFILE TAB
// ============================================
const ProfileTab = ({ user }) => {
  return (
    <div className="security-section">
      {/* User Avatar & Basic Info */}
      <div className="profile-card">
        <div className="profile-user-info">
          <div className="profile-avatar">
            {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div>
            <h2>
              {user?.name || 'User'}
            </h2>
            <p className="profile-user-email">
              {user?.email}
            </p>
            <div>
              <span className={`profile-user-role ${user?.isPlatformAdmin ? 'platform-admin' : ''}`}>
                {user?.isPlatformAdmin ? 'Platform Admin' : user?.role || 'User'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Account Information */}
      <div className="profile-card">
        <h3 className="profile-card-title">
          <Mail size={18} />
          Account Information
        </h3>
        
        <div className="profile-info-grid">
          <InfoField label="Full Name" value={user?.name} />
          <InfoField label="Email Address" value={user?.email} />
          <InfoField label="Username" value={user?.username || user?.email} />
          <InfoField label="Account Status" value="Active" isStatus />
        </div>
      </div>

      {/* Site Access */}
      {user?.tenantAccess?.length > 0 && (
        <div className="profile-card">
          <h3 className="profile-card-title">
            <Building2 size={18} />
            Site Access
          </h3>
          
          <div className="site-access-list">
            {user.tenantAccess.map((ta, idx) => (
              <div 
                key={idx}
                style={{
                  padding: '12px 16px',
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span className="site-access-name">{ta.tenantName}</span>
                <span className="site-access-role">
                  {ta.role}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const InfoField = ({ label, value, isStatus }) => (
  <div>
    <label style={{ 
      display: 'block', 
      color: 'var(--text-muted)', 
      fontSize: '12px', 
      marginBottom: '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }}>
      {label}
    </label>
    {isStatus ? (
      <span className="info-field-status">
        <CheckCircle size={14} />
        {value}
      </span>
    ) : (
      <span className="info-field-value">
        {value || '—'}
      </span>
    )}
  </div>
);

// ============================================
// SECURITY TAB
// ============================================
const SecurityTab = ({ user }) => {
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const fetchSessions = async () => {
    try {
      setLoadingSessions(true);
      const response = await fetch('/api/sessions/my-sessions', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setSessions(data.sessions);
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoadingSessions(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleRevokeSession = async (sessionId) => {
    if (!confirm('Sign out this device?')) return;
    
    setRevoking(sessionId);
    try {
      const response = await fetch(`/api/sessions/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setSessions(sessions.filter(s => s.id !== sessionId));
      }
    } catch (err) {
      alert('Failed to revoke session');
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAllOther = async () => {
    const otherCount = sessions.filter(s => !s.isCurrent).length;
    if (!confirm(`Sign out ${otherCount} other device${otherCount > 1 ? 's' : ''}?`)) return;
    
    setRevoking('all');
    try {
      const response = await fetch('/api/sessions/revoke-other', {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setSessions(sessions.filter(s => s.isCurrent));
      }
    } catch (err) {
      alert('Failed to revoke sessions');
    } finally {
      setRevoking(null);
    }
  };

  const getDeviceIcon = (device) => {
    switch (device?.type) {
      case 'Mobile': return <Smartphone size={20} />;
      case 'Tablet': return <Tablet size={20} />;
      default: return <Monitor size={20} />;
    }
  };

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diffMs = new Date() - new Date(dateStr);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const otherSessionsCount = sessions.filter(s => !s.isCurrent).length;

  return (
    <div className="security-section">
      {/* Two-Factor Authentication */}
      <MfaSetup />

      {/* Password Section */}
      <div className="profile-card">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: showPasswordForm ? '20px' : '0'
        }}>
          <div>
            <h3 className="profile-card-title" style={{ marginBottom: 0 }}>
              <KeyRound size={18} />
              Password
            </h3>
            <p className="profile-card-desc">
              Change your account password
            </p>
          </div>
          <button
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="btn btn-secondary"
          >
            {showPasswordForm ? 'Cancel' : 'Change Password'}
          </button>
        </div>

        {showPasswordForm && <ChangePasswordForm onSuccess={() => setShowPasswordForm(false)} />}
      </div>

      {/* Active Sessions Section */}
      <div className="profile-card">
        <div className="sessions-header">
          <div>
            <h3 className="profile-card-title" style={{ marginBottom: 0 }}>
              <Monitor size={18} />
              Active Sessions
            </h3>
            <p className="profile-card-desc">
              Devices where you're currently logged in
            </p>
          </div>
          <div className="sessions-actions">
            <button
              onClick={fetchSessions}
              disabled={loadingSessions}
              className="btn btn-secondary"
              >
              <RefreshCw size={14} className={loadingSessions ? 'spin' : ''} />
            </button>
            {otherSessionsCount > 0 && (
              <button
                onClick={handleRevokeAllOther}
                disabled={revoking === 'all'}
                className="btn"
                style={{ 
                  padding: '8px 12px',
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#f87171'
                }}
              >
                <Trash2 size={14} />
                Sign out all other ({otherSessionsCount})
              </button>
            )}
          </div>
        </div>

        {loadingSessions ? (
          <div className="profile-loading">
            <Loader2 size={24} className="spin" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="profile-empty">
            <p>No active sessions</p>
          </div>
        ) : (
          <div className="sessions-list">
            {sessions.map((session) => (
              <div
                key={session.id}
                className={`session-item ${session.isCurrent ? 'current' : ''}`}
              >
                <div className="session-icon">
                  {getDeviceIcon(session.device)}
                </div>
                
                <div className="session-details">
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginBottom: '2px',
                    flexWrap: 'wrap'
                  }}>
                    <span className="session-device-name">
                      {session.device?.browser || 'Unknown'} on {session.device?.os || 'Unknown'}
                    </span>
                    {session.isCurrent && (
                      <span className="session-current-badge">Current</span>
                    )}
                    {session.isExtended && (
                      <span className="session-extended-badge">30-day session</span>
                    )}
                  </div>
                  <div className="session-meta">
                    Last active: {getTimeAgo(session.lastActivityAt)}
                    {session.ipAddress && ` • ${session.ipAddress}`}
                  </div>
                </div>

                {!session.isCurrent && (
                  <button
                    onClick={() => handleRevokeSession(session.id)}
                    disabled={revoking === session.id}
                    className="btn profile-btn-secondary session-signout-btn"
                  >
                    {revoking === session.id ? <Loader2 size={12} className="spin" /> : 'Sign out'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

// ============================================
// CHANGE PASSWORD FORM
// ============================================
const ChangePasswordForm = ({ onSuccess }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword })
      });

      const data = await response.json();

      if (data.success) {
        alert('Password changed successfully!');
        onSuccess();
      } else {
        setError(data.error || 'Failed to change password');
      }
    } catch (err) {
      setError('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="profile-error-message">
          {error}
        </div>
      )}

      <div className="password-form">
        <div>
          <label className="profile-form-label">
            Current Password
          </label>
          <input
            type={showPasswords ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            className="profile-form-input"
          />
        </div>

        <div>
          <label className="profile-form-label">
            New Password
          </label>
          <input
            type={showPasswords ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={8}
            className="profile-form-input"
          />
        </div>

        <div>
          <label className="profile-form-label">
            Confirm New Password
          </label>
          <input
            type={showPasswords ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="profile-form-input"
          />
        </div>

        <label style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          color: 'var(--text-muted)',
          fontSize: '13px',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={showPasswords}
            onChange={(e) => setShowPasswords(e.target.checked)}
            style={{ accentColor: 'var(--accent-primary)' }}
          />
          Show passwords
        </label>

        <button
          type="submit"
          disabled={loading}
          className="btn profile-btn-primary mt-sm"
        >
          {loading ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
          {loading ? 'Saving...' : 'Update Password'}
        </button>
      </div>
    </form>
  );
};

const THEMES = [
  { id: 'indigo',   name: 'Indigo',   type: 'light', accent: '#6366F1', bg: '#F5F5F7', sidebar: '#1A1A2E' },
  { id: 'cobalt',   name: 'Cobalt',   type: 'light', accent: '#2563EB', bg: '#F5F5F7', sidebar: '#1A1A2E' },
  { id: 'violet',   name: 'Violet',   type: 'light', accent: '#7C3AED', bg: '#F5F5F7', sidebar: '#1A1A2E' },
  { id: 'emerald',  name: 'Emerald',  type: 'light', accent: '#059669', bg: '#F5F5F7', sidebar: '#1A1A2E' },
  { id: 'midnight', name: 'Midnight', type: 'dark',  accent: '#818CF8', bg: '#0F0F1A', sidebar: '#0A0A14' },
  { id: 'carbon',   name: 'Carbon',   type: 'dark',  accent: '#60A5FA', bg: '#0F0F1A', sidebar: '#0A0A14' },
];

const PreferencesTab = ({ user }) => {
  const { theme, setTheme } = useThemeStore();

  const handleThemeSelect = async (themeId) => {
    setTheme(themeId);
    // Persist to backend via existing user settings endpoint
    try {
      await fetch('/api/user/settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: { theme: themeId } }),
      });
    } catch (e) {
      // Silent fail — localStorage already persisted via themeStore
    }
  };

  return (
    <div className="preferences-section">
      <div className="profile-card">
        <h3 className="profile-card-title">
          <Palette size={18} />
          Appearance
        </h3>
        <p className="profile-card-desc">
          Choose a theme for the entire interface. Your selection is saved to your account.
        </p>
        <div className="theme-grid">
          {THEMES.map(t => {
            const selected = theme === t.id;
            return (
              <button
                key={t.id}
                type="button"
                className={`theme-card ${selected ? 'selected' : ''}`}
                onClick={() => handleThemeSelect(t.id)}
              >
                <div className="theme-card-preview">
                  <div className="theme-card-sidebar" style={{ background: t.sidebar }} />
                  <div className="theme-card-page" style={{ background: t.bg }}>
                    <div className="theme-card-accent" style={{ background: t.accent }} />
                    <div className="theme-card-line" />
                    <div className="theme-card-line short" />
                  </div>
                </div>
                <div className="theme-card-meta">
                  <div className="theme-card-name">{t.name}</div>
                  <div className="theme-card-type">{t.type}</div>
                </div>
                {selected && (
                  <div className="theme-card-check"><Check size={14} /></div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="profile-card">
        <h3 className="profile-card-title">
          <Globe size={18} />
          Regional Settings
        </h3>

        <div className="preference-list">
          <div style={{ 
            padding: '16px',
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: '500' }}>Time Format</div>
            </div>
            <select
              defaultValue="12h"
              className="form-input"
              style={{ width: 'auto', padding: '8px 12px' }}
            >
              <option value="12h">12-hour</option>
              <option value="24h">24-hour</option>
            </select>
          </div>

          <div style={{ 
            padding: '16px',
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: '500' }}>Date Format</div>
            </div>
            <select
              defaultValue="mdy"
              className="form-input"
              style={{ width: 'auto', padding: '8px 12px' }}
            >
              <option value="mdy">MM/DD/YYYY</option>
              <option value="dmy">DD/MM/YYYY</option>
              <option value="ymd">YYYY-MM-DD</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// TUTORIALS TAB
// ============================================
const TutorialsTab = () => {
  const loadProgress = useTutorialStore((s) => s.loadProgress);
  const progress = useTutorialStore((s) => s.progress);
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const resetModule = useTutorialStore((s) => s.resetModule);
  const resetAll = useTutorialStore((s) => s.resetAll);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const getStatus = (moduleKey) => {
    const rec = progress.get(moduleKey);
    if (rec?.completed) return { label: 'Completed', color: '#10b981', Icon: CheckCircle };
    if (rec?.dismissed) return { label: 'Dismissed', color: '#94a3b8', Icon: CircleDashed };
    return { label: 'Not started', color: '#94a3b8', Icon: CircleDashed };
  };

  const handleResetAll = async () => {
    if (!confirm('Reset all tutorials? They will re-prompt on next visit.')) return;
    await resetAll();
  };

  return (
    <div className="security-section">
      <div className="profile-card">
        <h3 className="profile-card-title">
          <BookOpen size={18} />
          Interactive Tutorials
        </h3>
        <p className="profile-card-desc">
          Take a guided tour of each module. Restart any tutorial to see it again.
        </p>

        <div className="tutorials-list">
          {allTutorials.map((t) => {
            const status = getStatus(t.module);
            const StatusIcon = status.Icon;
            const rec = progress.get(t.module);
            const hasRecord = !!rec;
            return (
              <div key={t.module} className="tutorial-row">
                <div className="tutorial-row-info">
                  <div className="tutorial-row-name">{t.moduleName}</div>
                  <div className="tutorial-row-status" style={{ color: status.color }}>
                    <StatusIcon size={13} />
                    <span>{status.label}</span>
                    <span className="tutorial-row-steps">{t.steps.length} step{t.steps.length === 1 ? '' : 's'}</span>
                  </div>
                </div>
                <div className="tutorial-row-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => startTutorial(t.module, t.steps)}
                    title="Start tutorial"
                  >
                    <Play size={13} /> Start
                  </button>
                  {hasRecord && (
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => resetModule(t.module)}
                      title="Reset this tutorial"
                    >
                      <RotateCcw size={13} /> Restart
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="tutorials-footer">
          <button className="btn btn-secondary" onClick={handleResetAll}>
            <RotateCcw size={14} /> Reset All Tutorials
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
